import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { ThemeProvider } from "next-themes";
import { LangProvider } from "@/hooks/useLang";
import { lazy, Suspense } from "react";
import { Shield } from "lucide-react";

// Eager — landing + always-mounted shells
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import SmartChat from "./components/SmartChat";
import FooterQAWidget from "./components/FooterQAWidget";
import BottomNav from "./components/BottomNav";
import SiteFooter from "./components/SiteFooter";
import { RequireSovereign } from "@/components/admin/RequireSovereign";

// Lazy — every other route
const OratoryStudio = lazy(() => import("./pages/OratoryStudio"));
const OratoryPractice = lazy(() => import("./pages/OratoryPractice"));
const FloorPractice = lazy(() => import("./pages/FloorPractice"));
const SituationRoom = lazy(() => import("./pages/SituationRoom"));
const Arena = lazy(() => import("./pages/Arena"));
const LiveAssist = lazy(() => import("./pages/LiveAssist"));
const ColoradoDemo = lazy(() => import("./pages/ColoradoDemo"));
const ChiefOfStaffPDF = lazy(() => import("./pages/ChiefOfStaffPDF"));
const LegalPage = lazy(() => import("./pages/LegalPage"));
const Auth = lazy(() => import("./pages/Auth"));
const Admin = lazy(() => import("./pages/Admin"));
const Community = lazy(() => import("./pages/Community"));
const Sovereign = lazy(() => import("./pages/Sovereign"));
const Livestream = lazy(() => import("./pages/Livestream"));
const FeedPage = lazy(() => import("./pages/FeedPage"));
const GoLive = lazy(() => import("./pages/GoLive"));
const ChallengeCreate = lazy(() => import("./pages/ChallengeCreate"));
const FloorHolder = lazy(() => import("./pages/FloorHolder"));
const FedRAMP = lazy(() => import("./pages/FedRAMP"));
const DebateForge = lazy(() => import("./pages/DebateForge"));
const DebateLobby = lazy(() => import("./pages/DebateLobby"));
const Pricing = lazy(() => import("./pages/Pricing"));
const TrustCenter = lazy(() => import("./pages/TrustCenter"));
const VideoHub = lazy(() => import("./pages/VideoHub"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const SovereignFeatures = lazy(() => import("./pages/SovereignFeatures"));
const Profile = lazy(() => import("./pages/Profile"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const AIAssistant = lazy(() => import("./pages/AIAssistant"));
const VirtualPodium = lazy(() => import("./pages/VirtualPodium"));
const CoachPage = lazy(() => import("./pages/CoachPage"));
const DebateRoom = lazy(() => import("./pages/DebateRoom"));
const Marketplace = lazy(() => import("./pages/Marketplace"));
const AdsManager = lazy(() => import("./pages/AdsManager"));
const LingualStudio = lazy(() => import("./pages/LingualStudio"));
const Gamification = lazy(() => import("./pages/Gamification"));
const CreatorStudio = lazy(() => import("./pages/CreatorStudio"));
const Bookmarks = lazy(() => import("./pages/Bookmarks"));
const AtlasCommand = lazy(() => import("./pages/AtlasCommand"));
const CheckoutReturn = lazy(() => import("./pages/CheckoutReturn"));
const WatchNow = lazy(() => import("./pages/WatchNow"));
const VirtualMeetings = lazy(() => import("./pages/VirtualMeetings"));
const SovereignEnhanced = lazy(() => import("./pages/SovereignEnhanced"));
const QuickLaunch = lazy(() => import("./pages/QuickLaunch"));
const RequestBriefing = lazy(() => import("./pages/RequestBriefing"));
const HostDebate = lazy(() => import("./pages/HostDebate"));
const NewCampaign = lazy(() => import("./pages/NewCampaign"));
const CampaignAnalytics = lazy(() => import("./pages/CampaignAnalytics"));
const NeuralSimulation = lazy(() => import("./pages/NeuralSimulation"));
const AITransparency = lazy(() => import("./pages/AITransparency"));
const DiplomaticGuard = lazy(() => import("./pages/DiplomaticGuard"));
const QuantumSecurity = lazy(() => import("./pages/QuantumSecurity"));
const AIPoll = lazy(() => import("./pages/AIPoll"));
const Scheduler = lazy(() => import("./pages/Scheduler"));
const AIDiscourseWriter = lazy(() => import("./pages/AIDiscourseWriter"));
const DebateViewer = lazy(() => import("./pages/DebateViewer"));
const DebatePlayer = lazy(() => import("./pages/DebatePlayer"));
const FloorTrainer = lazy(() => import("./pages/FloorTrainer"));
const CoachingDashboard = lazy(() => import("./pages/CoachingDashboard"));
const SmartChatPage = lazy(() => import("./pages/SmartChatPage"));
const CommentsPage = lazy(() => import("./pages/CommentsPage"));
const PollsPage = lazy(() => import("./pages/PollsPage"));
const StatsPage = lazy(() => import("./pages/StatsPage"));
const AIToolsPage = lazy(() => import("./pages/AIToolsPage"));
const TranslatePage = lazy(() => import("./pages/TranslatePage"));
const PresidentialRoom = lazy(() => import("./pages/PresidentialRoom"));
const WhatsAppCall = lazy(() => import("./pages/WhatsAppCall"));
const CEOPilot = lazy(() => import("./pages/CEOPilot"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const SurveyPage = lazy(() => import("./pages/SurveyPage"));
const AIMarketingHub = lazy(() => import("./pages/AIMarketingHub"));
const SurveyResults = lazy(() => import("./pages/SurveyResults"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const SystemAudit = lazy(() => import("./pages/SystemAudit"));
const VideoRouteHealth = lazy(() => import("./pages/VideoRouteHealth"));
const MuxMirrorsAdmin = lazy(() => import("./pages/MuxMirrorsAdmin"));
const InboundHmacAdmin = lazy(() => import("./pages/InboundHmacAdmin"));
const RemainingBeforePublish = lazy(() => import("./pages/RemainingBeforePublish"));
const PipelineStatus = lazy(() => import("./pages/PipelineStatus"));
const RlsMatrixUploader = lazy(() => import("./pages/RlsMatrixUploader"));
const AuthFailureLog = lazy(() => import("./pages/AuthFailureLog"));
const CIArtifactViewer = lazy(() => import("./pages/CIArtifactViewer"));
const CIFixtureSourceViewer = lazy(() => import("./pages/CIFixtureSourceViewer"));
const SelfDebatePrivacyMetrics = lazy(() => import("./pages/SelfDebatePrivacyMetrics"));
const AdminRetentionAudit = lazy(() => import("./pages/AdminRetentionAudit"));
const SentryDiagnostics = lazy(() => import("./pages/SentryDiagnostics"));
const FactCheckAudit = lazy(() => import("./pages/FactCheckAudit"));

const queryClient = new QueryClient();

const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-3">
      <Shield className="w-8 h-8 text-sovereign-gold animate-pulse" />
      <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Loading…</p>
    </div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="dark" storageKey="thefloor-theme">
    <LangProvider>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/oratory-studio" element={<OratoryStudio />} />
              <Route path="/oratory-practice" element={<OratoryPractice />} />
              <Route path="/floor-practice" element={<FloorPractice />} />
              <Route path="/situation-room" element={<SituationRoom />} />
              <Route path="/arena" element={<Arena />} />
              <Route path="/live-assist" element={<LiveAssist />} />
              <Route path="/community" element={<Community />} />
              <Route path="/feed/:type" element={<FeedPage />} />
              <Route path="/livestream" element={<Livestream />} />
              <Route path="/demo" element={<ColoradoDemo />} />
              <Route path="/brief" element={<ChiefOfStaffPDF />} />
              <Route path="/sovereign" element={<Sovereign />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/legal/:slug" element={<LegalPage />} />
              <Route path="/go-live" element={<GoLive />} />
              <Route path="/challenge/create" element={<ChallengeCreate />} />
              <Route path="/floor-holders" element={<FloorHolder />} />
              <Route path="/fedramp" element={<FedRAMP />} />
              <Route path="/debate-forge" element={<DebateForge />} />
              <Route path="/debate-lobby" element={<DebateLobby />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/trust-center" element={<TrustCenter />} />
              <Route path="/video-hub" element={<VideoHub />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/sovereign-features" element={<SovereignFeatures />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/ai-assistant" element={<AIAssistant />} />
              <Route path="/virtual-podium" element={<VirtualPodium />} />
              <Route path="/coach" element={<CoachPage />} />
              <Route path="/debate-room" element={<DebateRoom />} />
              <Route path="/marketplace" element={<Marketplace />} />
              <Route path="/ads-manager" element={<AdsManager />} />
              <Route path="/lingual-studio" element={<LingualStudio />} />
              <Route path="/gamification" element={<Gamification />} />
              <Route path="/creator" element={<CreatorStudio />} />
              <Route path="/bookmarks" element={<Bookmarks />} />
              <Route path="/atlas-command" element={<AtlasCommand />} />
              <Route path="/checkout/return" element={<CheckoutReturn />} />
              <Route path="/watch" element={<WatchNow />} />
              <Route path="/virtual-meetings" element={<VirtualMeetings />} />
              <Route path="/sovereign-enhanced" element={<SovereignEnhanced />} />
              <Route path="/quick-launch" element={<QuickLaunch />} />
              <Route path="/request-briefing" element={<RequestBriefing />} />
              <Route path="/host-debate" element={<HostDebate />} />
              <Route path="/ads-manager/new" element={<NewCampaign />} />
              <Route path="/ads-manager/analytics" element={<CampaignAnalytics />} />
              <Route path="/neural-simulation" element={<NeuralSimulation />} />
              <Route path="/ai-transparency" element={<AITransparency />} />
              <Route path="/diplomatic-guard" element={<DiplomaticGuard />} />
              <Route path="/quantum-security" element={<QuantumSecurity />} />
              <Route path="/ai-poll" element={<AIPoll />} />
              <Route path="/scheduler" element={<Scheduler />} />
              <Route path="/ai-discourse-writer" element={<AIDiscourseWriter />} />
              <Route path="/debate-viewer" element={<DebateViewer />} />
              <Route path="/debate-player" element={<DebatePlayer />} />
              <Route path="/floor-trainer" element={<FloorTrainer />} />
              <Route path="/coaching-dashboard" element={<CoachingDashboard />} />
              <Route path="/smart-chat" element={<SmartChatPage />} />
              <Route path="/comments" element={<CommentsPage />} />
              <Route path="/polls" element={<PollsPage />} />
              <Route path="/chat-stats" element={<StatsPage />} />
              <Route path="/ai-tools" element={<AIToolsPage />} />
              <Route path="/translate" element={<TranslatePage />} />
              <Route path="/presidential-room" element={<PresidentialRoom />} />
              <Route path="/whatsapp" element={<WhatsAppCall />} />
              <Route path="/ceo-pilot" element={<CEOPilot />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/survey" element={<SurveyPage />} />
              <Route path="/ai-marketing" element={<AIMarketingHub />} />
              <Route path="/survey-results" element={<SurveyResults />} />
              <Route path="/admin/users" element={<AdminUsers />} />
              <Route path="/admin/system-audit" element={<SystemAudit />} />
              <Route path="/admin/video-routes" element={<VideoRouteHealth />} />
              <Route path="/admin/mux-mirrors" element={<MuxMirrorsAdmin />} />
              <Route path="/admin/inbound-hmac" element={<InboundHmacAdmin />} />
              <Route path="/admin/remaining-before-publish" element={<RemainingBeforePublish />} />
              <Route path="/admin/pipeline-status" element={<PipelineStatus />} />
              <Route path="/admin/rls-matrix" element={<RlsMatrixUploader />} />
              <Route path="/admin/auth-failures" element={<AuthFailureLog />} />
              <Route path="/admin/ci-artifacts" element={<RequireSovereign><CIArtifactViewer /></RequireSovereign>} />
              <Route path="/admin/ci-fixture" element={<RequireSovereign><CIFixtureSourceViewer /></RequireSovereign>} />
              <Route path="/metrics/self-debate-privacy" element={<SelfDebatePrivacyMetrics />} />
              <Route path="/admin/retention-audit" element={<RequireSovereign><AdminRetentionAudit /></RequireSovereign>} />
              <Route path="/dev/sentry" element={<SentryDiagnostics />} />
              <Route path="/admin/factcheck-audit" element={<RequireSovereign><FactCheckAudit /></RequireSovereign>} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <SiteFooter />
          <SmartChat />
          <FooterQAWidget />
          <BottomNav />
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
    </LangProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
